// UploadProgress.js — Drop this file into your src/ folder
// Upload progress overlay with per-file status

import { useState, useEffect } from 'react';

const STAGE_LABELS = {
  queued: 'Queued',
  compressing: 'Compressing...',
  uploading: 'Uploading...',
  complete: 'Done ✓',
  error: 'Failed ✗',
  duplicate: 'Already uploaded',
};

function UploadProgress({ uploadQueue, isUploading, totalSaved, duplicateCount }) {
  if (!isUploading || uploadQueue.length === 0) return null;

  const completed = uploadQueue.filter(f => f.stage === 'complete').length;
  const failed = uploadQueue.filter(f => f.stage === 'error').length;
  const totalProgress = uploadQueue.length > 0
    ? (completed / uploadQueue.length) * 100
    : 0;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.title}>
          Uploading Memories
        </h2>
        <p style={styles.subtitle}>
          {completed} of {uploadQueue.length} complete
          {failed > 0 && <span style={styles.failedText}> · {failed} failed</span>}
          {duplicateCount > 0 && (
            <span style={styles.duplicateText}> · {duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''} skipped</span>
          )}
          {totalSaved > 0 && (
            <span style={styles.savedText}>
              {' '}· Saved {(totalSaved / 1024 / 1024).toFixed(0)}MB with compression
            </span>
          )}
        </p>

        {/* Overall progress bar */}
        <div style={styles.overallBarBg}>
          <div 
            style={{
              ...styles.overallBarFill,
              width: `${totalProgress}%`,
            }}
          />
        </div>

        {/* Per-file list */}
        <div style={styles.fileList}>
          {uploadQueue.map((item, index) => (
            <div key={index} style={styles.fileRow}>
              <div style={styles.fileInfo}>
                <span style={styles.fileIcon}>
                  {item.type === 'video' ? '🎬' : '📸'}
                </span>
                <span style={styles.fileName}>
                  {truncateName(item.name, 30)}
                </span>
                <span style={styles.fileSize}>
                  {(item.size / 1024 / 1024).toFixed(1)}MB
                </span>
              </div>
              <div style={styles.fileStatus}>
                <span style={{
                  ...styles.stageLabel,
                  color: item.stage === 'complete' ? '#4ade80' 
                       : item.stage === 'error' ? '#f87171'
                       : '#00f3ff',
                }}>
                  {STAGE_LABELS[item.stage] || item.stage}
                </span>
                {(item.stage === 'uploading' || item.stage === 'compressing') && (
                  <div style={styles.fileBarBg}>
                    <div 
                      style={{
                        ...styles.fileBarFill,
                        width: `${item.progress || 0}%`,
                        backgroundColor: item.stage === 'compressing' ? '#b537f2' : '#00f3ff',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p style={styles.tip}>
          💡 Photos are compressed automatically for faster uploads. For best results, upload videos one at a time.
        </p>
      </div>
    </div>
  );
}

function truncateName(name, maxLen) {
  if (name.length <= maxLen) return name;
  const ext = name.split('.').pop();
  return name.substring(0, maxLen - ext.length - 4) + '...' + ext;
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(10px)',
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '20px',
    padding: '30px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(0, 243, 255, 0.2)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },
  title: {
    color: '#fff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 5px 0',
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '14px',
    margin: '0 0 20px 0',
    textAlign: 'center',
  },
  failedText: {
    color: '#f87171',
  },
  savedText: {
    color: '#4ade80',
  },
  duplicateText: {
    color: '#fbbf24',
  },
  overallBarBg: {
    width: '100%',
    height: '6px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  overallBarFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #ff006e, #b537f2, #00f3ff)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  fileList: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '400px',
    paddingRight: '5px',
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  fileIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  fileName: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '11px',
    flexShrink: 0,
    marginLeft: '5px',
  },
  fileStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
    marginLeft: '10px',
    minWidth: '120px',
    justifyContent: 'flex-end',
  },
  stageLabel: {
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  fileBarBg: {
    width: '60px',
    height: '4px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  fileBarFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.2s ease',
  },
  tip: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    textAlign: 'center',
    marginTop: '15px',
    marginBottom: 0,
  },
};

export default UploadProgress;